import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Axios } from "api/axios";
import MasterForm from "../Enquiry/MasterFrom";
import moment from "moment";
import { fetchEnquiryById } from "slices/enquiry/thunk";

const SiteVisitEditFrom: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { id } = useParams<{ id: string }>();
  const [initialData, setInitialData] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);

  // Redux selector for enquiry state
  const selectEnquiryData = createSelector(
    (state: any) => state.Enquiry,
    (enquiry) => ({
      selectedEnquiry: enquiry.selectedEnquiry,
      loading: enquiry.loading,
      error: enquiry.error,
    })
  );

  const { selectedEnquiry, loading, error } = useSelector(selectEnquiryData);

  // Fetch enquiry data using Redux thunk
  useEffect(() => {
    if (!id) {
      toast.error("Site visit ID is missing");
      navigate("/sitevisit");
      return;
    }

    // Dispatch thunk to fetch enquiry by ID
    dispatch(fetchEnquiryById(id));
  }, [id, dispatch, navigate]);

  // Helper function to convert URL string to File object
  const urlToFile = async (
    url: string,
    filename: string
  ): Promise<File | null> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const file = new File([blob], filename, { type: blob.type });
      return file;
    } catch (error) {
      console.error("Failed to convert URL to File:", error);
      return null;
    }
  };

  // Format enquiry data when it's loaded
  useEffect(() => {
    const formatEnquiryData = async () => {
      if (selectedEnquiry) {
        // Thunk already extracts response.data.data or response.data
        const responseData = selectedEnquiry;

        // Convert item_file URLs to File objects
        const itemsWithFiles = await Promise.all(
          (responseData.enquiry_items || []).map(async (item: any) => {
            let itemFile: File | string | null = null;

            // If item_file is a URL string, convert it to File object
            if (
              item.item_file &&
              typeof item.item_file === "string" &&
              item.item_file.startsWith("http")
            ) {
              const filename =
                item.item_file.split("/").pop() ||
                `item_file_${item.enquiry_item_id || Date.now()}`;
              const file = await urlToFile(item.item_file, filename);
              if (file) {
                itemFile = file;
              } else {
                // If conversion fails, keep as string URL
                itemFile = item.item_file;
              }
            } else {
              itemFile = item.item_file || null;
            }

            return {
              id: item.enquiry_item_id,
              category: item.category_name || "",
              category_id: item.category_id || null,
              sub_category: item.sub_category_name || "",
              sub_category_id: item.sub_category_id || null,
              item_name: item.item_name || "",
              item_id: item.item_id || null,
              item_code: item.item_code || "",
              // Extract unit from denormalized fields
              unit: item.unit_name || "",
              unit_id: item.unit_id || null,
              // Extract HSN details from denormalized fields
              hsn_code_id: item.hsn_code_id || null,
              hsn_code_name: item.hsn_code_name || "",
              gst_percent: item.gst_percent
                ? parseFloat(String(item.gst_percent))
                : null,
              gst_id: item.gst_id || null,
              // Extract selling_price from denormalized fields
              selling_price: item.selling_price
                ? parseFloat(String(item.selling_price))
                : undefined,
              length: item.length ? String(item.length) : "",
              width: item.width ? String(item.width) : "",
              height: item.height ? String(item.height) : "",
              required_quantity: item.required_quantity
                ? String(item.required_quantity)
                : "",
              item_file: itemFile, // Now a File object if URL was converted successfully
              remarks: item.remarks || "",
            };
          })
        );

        // Format the enquiry data for MasterForm based on backend response structure
        const formattedData = {
          enquiry_date: responseData.enquiry_date
            ? moment(responseData.enquiry_date).format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD"),
          source_of_enquiry: responseData.enquiry_source || "",
          customer_name: responseData.customer_details?.customer_name || "",
          customer_id: responseData.customer_details?.customer_id || null,
          contact_person_name:
            responseData.customer_details?.contact_person_name || "",
          contact_person_phone_no:
            responseData.customer_details?.contact_person_phone_no || "",
          remark: responseData.remark || "",
          site_visit_enabled: responseData.site_visit_required || false,
          // Convert site_visit object to array format (if exists)
          site_visits: responseData.site_visit
            ? [
                {
                  id: responseData.site_visit?.site_visit_id || undefined,
                  site_visit_date: responseData.site_visit?.site_visit_date
                    ? moment(responseData.site_visit.site_visit_date).format(
                        "YYYY-MM-DD"
                      )
                    : "",
                  staff_name: responseData.site_visit?.staff_name || "",
                  staff_id: responseData.site_visit?.staff_id || undefined,
                  staff_country_code:
                    responseData.site_visit?.staff_country_code || "",
                  staff_contact_number:
                    responseData.site_visit?.staff_contact_number || "",
                  staff_email: responseData.site_visit?.staff_email || "",
                  location_url: responseData.site_visit?.location_url || "",
                  address: responseData.site_visit?.address || "",
                  city: responseData.site_visit?.city || "",
                  state: responseData.site_visit?.state || "",
                  remarks: responseData.site_visit?.remarks || "",
                },
              ]
            : [],
          items: itemsWithFiles,
        };

        setInitialData(formattedData);
      }
    };

    formatEnquiryData();
  }, [selectedEnquiry]);

  // Handle errors
  useEffect(() => {
    if (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.error ||
            error?.message ||
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to fetch site visit";
      toast.error(errorMessage);
      navigate("/sitevisit");
    }
  }, [error, navigate]);

  // Handle form submission
  const handleSubmit = async (formData: any) => {
    if (!id) {
      toast.error("Site visit ID is missing");
      return;
    }

    try {
      setSubmitting(true);
      // Prepare enquiry items - item_file will be handled separately in FormData
      const enquiryItems = formData.items.map((item: any) => {
        return {
          category_id: item.category_id || null,
          category_name: item.category || "",
          sub_category_id: item.sub_category_id || null,
          sub_category_name: item.sub_category || "",
          item_id: item.item_id || null,
          item_name: item.item_name || "",
          item_code: item.item_code || "",
          item_details: item.item_details || null,
          // Denormalized HSN and GST fields
          hsn_code_id: item.hsn_code_id || null,
          hsn_code_name: item.hsn_code_name || "",
          gst_id: item.gst_id || null,
          gst_percent: item.gst_percent || null,
          // Denormalized unit fields
          unit_id: item.unit_id || null,
          unit_name: item.unit || "",
          // Selling price (not shown in UI but required in payload)
          selling_price: item.selling_price || null,
          length: item.length || null,
          width: item.width || null,
          height: item.height || null,
          required_quantity: item.required_quantity || "",
          remarks: item.remarks || null,
          // item_file will be handled separately in FormData
        };
      });

      // Prepare customer details object (from form data, not editable but needed for backend)
      const customerDetails = {
        customer_id: formData.customer_id || null,
        customer_name: formData.customer_name || "",
        contact_person_name: formData.contact_person_name || "",
        contact_person_phone_no: formData.contact_person_phone_no || "",
      };

      // Prepare site visit object (single object, not array - based on backend)
      let siteVisit = null;
      if (
        formData.site_visit_enabled &&
        formData.site_visits &&
        formData.site_visits.length > 0
      ) {
        const siteVisitData = formData.site_visits[0]; // Take first site visit
        siteVisit = {
          staff_id: siteVisitData.staff_id || null,
          staff_details: {
            user_id: siteVisitData.staff_id || null,
            staff_name: siteVisitData.staff_name || "",
          },
          // Denormalized staff fields
          staff_name: siteVisitData.staff_name || null,
          staff_country_code: siteVisitData.staff_country_code || null,
          staff_contact_number: siteVisitData.staff_contact_number || null,
          staff_email: siteVisitData.staff_email || null,
          site_visit_date:
            siteVisitData.site_visit_date || moment().format("YYYY-MM-DD"),
          location_url: siteVisitData.location_url || null,
          address: siteVisitData.address || null,
          city: siteVisitData.city || null,
          state: siteVisitData.state || null,
          remarks: siteVisitData.remarks || null,
        };
      }

      // Create FormData - Backend expects individual fields, not wrapped in "data"
      const formDataPayload = new FormData();

      // Append individual fields as the backend expects them
      formDataPayload.append(
        "enquiry_date",
        formData.enquiry_date || moment().format("YYYY-MM-DD")
      );
      if (formData.source_of_enquiry) {
        formDataPayload.append("enquiry_source", formData.source_of_enquiry);
      }
      if (formData.remark) {
        formDataPayload.append("remark", formData.remark);
      }

      // Append JSON fields as strings (backend will parse them)
      formDataPayload.append(
        "customer_details",
        JSON.stringify(customerDetails)
      );
      formDataPayload.append(
        "site_visit_required",
        formData.site_visit_enabled ? "true" : "false"
      );
      formDataPayload.append("enquiry_items", JSON.stringify(enquiryItems));

      if (siteVisit) {
        formDataPayload.append("site_visit", JSON.stringify(siteVisit));
      }

      // Append files for each item
      // Each item has a single file in item_file field
      formData.items.forEach((item: any, itemIndex: number) => {
        if (item.item_file && item.item_file instanceof File) {
          // Append single file with field name matching backend expectation
          formDataPayload.append(
            `enquiry_items[${itemIndex}][item_file]`,
            item.item_file
          );
        }
      });

      formDataPayload.append("is_completed", "true");

      // Send FormData
      const response = await Axios.put(
        `/enquiry/${id}/update/`,
        formDataPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data) {
        toast.success("Site visit updated successfully!");
        navigate("/sitevisit");
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.error ||
        error?.message ||
        "Failed to update site visit";
      toast.error(errorMessage);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  if (!initialData && loading) {
    return null;
  }

  // Note: Child components handle their own data fetching:
  // - CustomerDetailsSection: fetches customers if not provided
  // - ItemDetailsSection: fetches categories using Redux thunk
  // - SiteVisitSection: fetches labour dropdown using Redux thunk
  // - MasterForm: uses hardcoded commonSources for enquiry sources
  return (
    <MasterForm
      initialData={initialData}
      isEdit={true}
      enquiryId={id}
      onSubmit={handleSubmit}
      loading={loading}
      itemsOnlyEdit={true}
    />
  );
};

export default SiteVisitEditFrom;
